<script lang="ts">
	/**
	 * One dimension, ranked, with the chart and the table it was drawn from (§12.10).
	 *
	 * Used for both acquisition and audience, because they are the same shape of question asked
	 * about different columns — a panel per dimension would be nine near-identical components that
	 * drift, exactly as nine endpoints would have been.
	 *
	 * **A session dimension carries purchases and revenue; an event dimension does not**, and the
	 * columns say so by being absent rather than by showing a zero. That is the whole reason the
	 * server splits the two: a visit converts, a page view does not, and a "0 purchases" beside a
	 * page's view count would be a claim rather than a gap.
	 */

	import {
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import { BreakdownChart, CountryMap, EmptyState, Panel, SelectField } from '../../../components';
	import { formatAmount } from '../../../stores/storefront';
	import type { AnalyticsBreakdown, AnalyticsQuery } from '../../../AppTypes';
	import { getAnalyticsBreakdown } from '../../../AppNetwork';

	export let filter: AnalyticsQuery;
	export let title: string = '';
	export let faIcon: string = 'chart-simple';
	/** The dimensions this panel offers, first one selected. */
	export let dimensions: { value: string; name: string }[] = [];
	export let help: string = '';

	let dimension: string = dimensions[0]?.value ?? '';

	/**
	 * A `for`/`id` pair unique to this instance.
	 *
	 * Derived from the **title**, which is what actually distinguishes the three copies of this
	 * panel on the page — the icon happens to differ today and is a presentation choice that could
	 * stop differing tomorrow, at which point two labels would point at one control and a click on
	 * the wrong one would move the wrong panel's dropdown.
	 */
	$: controlID = `breakdown-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
	let data: AnalyticsBreakdown | null = null;
	let error = '';
	let loading = false;

	$: queryKey = JSON.stringify({ filter, dimension });
	$: void fnLoad(queryKey);

	async function fnLoad(_key: string): Promise<void> {
		if (dimension === '') return;
		loading = true;
		try {
			// Every country for the map, not the top fifteen — a map with most of the world blank
			// because it fell outside a bar chart's cut reads as nobody visiting from there.
			data = await getAnalyticsBreakdown(filter, dimension, dimension === 'country' ? 250 : 15);
			error = '';
		} catch (caught) {
			error = caught instanceof Error ? caught.message : 'Failed to load the breakdown';
			data = null;
		} finally {
			loading = false;
		}
	}

	$: measure = (data?.scope === 'event' ? 'events' : 'sessions') as 'events' | 'sessions';
	/** The bar chart and the table keep their top fifteen; only the map takes the whole list. */
	$: rows = (data?.items ?? []).slice(0, 15);
	$: countryValues =
		dimension === 'country'
			? Object.fromEntries(
					(data?.items ?? [])
						.filter(row => row.value !== null)
						.map(row => [String(row.value).toUpperCase(), measure === 'events' ? row.events : row.sessions])
				)
			: {};
	$: measureLabel = data?.scope === 'event' ? 'Events' : 'Visits';

	function fnMoney(amounts: { currency: string; amount: number }[] | null): string {
		if (amounts === null || amounts.length === 0) return '—';
		return amounts
			.map(entry => $formatAmount(entry.amount, entry.currency) ?? `${entry.amount} ${entry.currency}`)
			.join(' · ');
	}
</script>

<Panel {title} {faIcon}>
	<svelte:fragment slot="titleRight">
		<!--
			Labelled inline rather than left as a bare dropdown, for the reason the filter bar's
			controls are: a control in a header with only a selected option to go on is a control
			whose meaning changes every time somebody uses it. `for`/`id` rather than a wrapping
			`<label>`, because a label wrapping a select in a panel title is itself a click target
			for the panel's toggle.
		-->
		<label class="text-xs text-default shrink-0" for={controlID}>Break down by</label>
		<SelectField
			id={controlID}
			size="sm"
			class="w-52"
			bind:value={dimension}
			items={dimensions}
		/>
	</svelte:fragment>

	{#if help.length > 0}
		<p class="text-default text-xs mb-2">{help}</p>
	{/if}

	{#if error.length > 0}
		<EmptyState variant="error" title="This breakdown could not be loaded." description={error} />
	{:else if data === null}
		<EmptyState variant="loading" />
	{:else if data.items.length === 0}
		<EmptyState
			variant="empty"
			title="Nothing recorded in this range."
			description={data.notApplicable > 0
				? `${data.notApplicable.toLocaleString()} events in this range are not about ${
						dimensions.find(entry => entry.value === dimension)?.name.toLowerCase() ?? 'this'
					}, so they are counted in the panels that are.`
				: ''}
			compact
		/>
	{:else}
		<div class:opacity-50={loading}>
			{#if dimension === 'country'}
				<CountryMap values={countryValues} measureLabel={measureLabel.toLowerCase()} />
			{/if}
			<BreakdownChart {rows} {title} {measure} />
		</div>

		{#if data.notApplicable > 0}
			<!--
				Stated, never bucketed. A `catalog_view` names no product and a `page_view` names no
				currency; counting them as "Unknown" made a category that is not one the largest bar
				on the chart. Silently dropping them would be the other half of the same mistake.
			-->
			<p class="text-default text-xs mt-2">
				{data.notApplicable.toLocaleString()} further
				{data.notApplicable === 1 ? 'event' : 'events'} in this range
				{data.notApplicable === 1 ? 'is' : 'are'} not about
				{dimensions.find(entry => entry.value === dimension)?.name.toLowerCase() ?? 'this'} —
				a catalogue view names no product, a page view names no currency — so they are left out
				rather than counted as unknown.
			</p>
		{/if}

		<div class="overflow-x-auto mt-3">
			<Table class="dataTable">
				<TableHead>
					<TableHeadCell>{dimensions.find(d => d.value === dimension)?.name ?? 'Value'}</TableHeadCell>
					<TableHeadCell class="text-right">{measureLabel}</TableHeadCell>
					{#if data.scope === 'session'}
						<TableHeadCell class="text-right">Purchases</TableHeadCell>
						<TableHeadCell class="text-right">Revenue</TableHeadCell>
					{/if}
				</TableHead>
				<TableBody>
					{#each rows as row (row.value ?? 'unknown')}
						<TableBodyRow>
							<!-- "Unknown" is a real answer here: a direct visit has no referrer, and a
							     country with no CDN header behind it has no country. -->
							<TableBodyCell class="dataTable">
								{row.label.length === 0 ? 'Unknown' : row.label}
							</TableBodyCell>
							<TableBodyCell class="dataTable text-right tabular-nums">
								{(measure === 'events' ? row.events : row.sessions).toLocaleString()}
							</TableBodyCell>
							{#if data.scope === 'session'}
								<TableBodyCell class="dataTable text-right tabular-nums">
									{(row.purchases ?? 0).toLocaleString()}
								</TableBodyCell>
								<TableBodyCell class="dataTable text-right tabular-nums">
									{fnMoney(row.revenue)}
								</TableBodyCell>
							{/if}
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</div>
	{/if}
</Panel>
